import React, { useEffect, useState } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useNavigate, useParams } from "react-router-dom";
import { FiSave, FiX, FiArrowLeft } from "react-icons/fi";
import "./AddScopeModule.css";

function AddScopeModule() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditMode);
  const [scopes, setScopes] = useState([]);
  const [modules, setModules] = useState([]);
  const [originalRecord, setOriginalRecord] = useState(null);

  const [formData, setFormData] = useState({
    scope: "",
    form_module: "",
  });

  useEffect(() => {
    fetchScopes();
    fetchModules();

    if (isEditMode) {
      fetchScopeModule();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchScopes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/scope-catalogs/`);
      setScopes(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching scope catalogs:", error);
      Swal.fire("Error", "Failed to load scope catalog list", "error");
    }
  };

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_BASE}/form-modules/`);
      setModules(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching form modules:", error);
      Swal.fire("Error", "Failed to load form module list", "error");
    }
  };

  const fetchScopeModule = async () => {
    setFetching(true);

    try {
      const res = await axios.get(`${API_BASE}/scope-modules/${id}/`);
      const item = res.data?.data || res.data;

      setOriginalRecord(item);

      setFormData({
        scope: item.scope ?? "",
        form_module: item.form_module ?? "",
      });
    } catch (error) {
      console.error("Error fetching scope module:", error);

      Swal.fire(
        "Error",
        "Failed to load scope module details",
        "error"
      );

      navigate("/catalog/scope-modules");
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.scope || !formData.form_module) {
      Swal.fire(
        "Validation Error",
        "Scope Catalog and Form Module are required",
        "warning"
      );
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    setLoading(true);

    try {
      const payload = {
        sequence: originalRecord?.sequence ?? 0,
        is_required: originalRecord?.is_required ?? true,
        activation_rule: originalRecord?.activation_rule || "",
        effective_from: originalRecord?.effective_from || today,
        effective_to: originalRecord?.effective_to || null,
        scope: Number(formData.scope),
        form_module: Number(formData.form_module),
        created_by: originalRecord?.created_by ?? 1,
      };

      if (isEditMode) {
        await axios.put(`${API_BASE}/scope-modules/${id}/`, payload);

        Swal.fire({
          icon: "success",
          title: "Updated!",
          text: "Scope module has been updated.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await axios.post(`${API_BASE}/scope-modules/`, payload);

        Swal.fire({
          icon: "success",
          title: "Added!",
          text: "Scope module has been created.",
          timer: 2000,
          showConfirmButton: false,
        });
      }

      navigate("/catalog/scope-modules");
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} scope module:`,
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          `Failed to ${
            isEditMode ? "update" : "create"
          } scope module`,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/catalog/scope-modules");
  };

  if (fetching) {
    return (
      <div className="app-layout">
        <Sidebar />

        <div className="main-content">
          <Header />

          <div className="scope-module-form-page">
            <div className="scope-module-form-loader">
              <div className="scope-module-form-spinner"></div>
              <p>Loading scope module...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="scope-module-form-page">
          <div className="scope-module-form-top">
            <button
              type="button"
              className="scope-module-form-back-btn"
              onClick={handleBack}
              title="Back"
            >
              <FiArrowLeft />
            </button>

            <div className="scope-module-form-page-heading">
              <h2>
                {isEditMode
                  ? "Edit Scope Module"
                  : "Add Scope Module"}
              </h2>
            </div>
          </div>

          <div className="scope-module-form-card">
            <form onSubmit={handleSubmit}>
              <div className="scope-module-form-grid">
                <div className="scope-module-form-field">
                  <label htmlFor="scope">
                    Scope Catalog{" "}
                    <span className="scope-module-form-required">
                      *
                    </span>
                  </label>

                  <select
                    id="scope"
                    name="scope"
                    value={formData.scope}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      Select scope catalog
                    </option>

                    {scopes.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.scope_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="scope-module-form-field">
                  <label htmlFor="form_module">
                    Form Module{" "}
                    <span className="scope-module-form-required">
                      *
                    </span>
                  </label>

                  <select
                    id="form_module"
                    name="form_module"
                    value={formData.form_module}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      Select form module
                    </option>

                    {modules.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.module_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="scope-module-form-actions">
                <button
                  type="button"
                  className="scope-module-form-cancel-btn"
                  onClick={handleBack}
                  disabled={loading}
                >
                  <FiX />
                  Cancel
                </button>

                <button
                  type="submit"
                  className="scope-module-form-save-btn"
                  disabled={loading}
                >
                  <FiSave />

                  {loading
                    ? "Saving..."
                    : isEditMode
                    ? "Update Scope Module"
                    : "Save Scope Module"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddScopeModule;